import { apiRequest } from '../../api/client';
import { useSession } from '../../auth/session';

const unwrap = async <T>(promise: Promise<{ data: T }>): Promise<T> => (await promise).data;

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/** Hóa đơn khách tự quét đang chờ thu ngân / quản lý chi nhánh quyết (BR-209). */
export interface PendingMemberScan {
  billScanId: string;
  memberId: string;
  memberName: string;
  memberPhone: string;
  amount: number;
  billRef: string;
  scannedAt: string;
  flags: string[];
  hasImage: boolean;
}

export type ReversalStatus = 'Pending' | 'Approved' | 'Rejected';

export interface ReversalListItem {
  id: string;
  billScanId: string;
  branchName: string;
  memberCode: string;
  billAmount: number;
  spendablePoints: number;
  reason: string;
  status: ReversalStatus;
  requestedBy: string;
  requestedAt: string;
}

export interface ReversalListResponse {
  items: ReversalListItem[];
  page: number;
  pageSize: number;
  total: number;
  pendingCount: number;
}

export interface ReversalDetail {
  id: string;
  billScanId: string;
  memberId: string;
  spendablePoints: number;
  tierPoints: number;
  status: ReversalStatus;
  reason: string;
  requestedAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
  warning: string | null;
}

export interface BillScanImage {
  url: string;
  expiresAt: string;
}

/** Một dòng trong danh sách hóa đơn quét cho màn giám sát (`S3-BE-07`). */
export interface BillScanListItem {
  id: string;
  billRef: string;
  branchId: string;
  branchName: string;
  memberId: string;
  memberCode: string;
  memberName: string;
  /** Tên nhân viên quét; null khi khách tự quét. */
  staffName: string | null;
  amount: number;
  pointsAwarded: number;
  status: string;
  source: 'Staff' | 'Member';
  flags: string[];
  hasImage: boolean;
  scannedAt: string;
  billIssuedAt: string | null;
  confirmedAt: string | null;
  rejectReason: string | null;
}

export interface BillScanListResponse {
  items: BillScanListItem[];
  limit: number;
  /** Con trỏ trang kế; null khi hết dòng. */
  nextCursor: string | null;
  flaggedCount: number;
  pendingConfirmationCount: number;
}

/** Bộ lọc danh sách hóa đơn quét. Ngày theo lịch VN (yyyy-MM-dd). */
export interface BillScanListFilters {
  branchIds?: string[];
  from?: string;
  to?: string;
  status?: string;
  source?: 'Staff' | 'Member';
  hasFlags?: boolean;
  flag?: string;
  memberId?: string;
}

/** Chỉ số tỷ lệ gắn cờ theo ngày (`S3-BE-07`). */
export interface BillScanStats {
  from: string;
  to: string;
  selfScanTotal: number;
  selfScanFlagged: number;
  flaggedRatePercent: number;
  alertThresholdPercent: number;
  overThreshold: boolean;
  requireStaffConfirmationOn: boolean;
  suggestEnableConfirmation: boolean;
}

/** Đối soát cuối ngày (`S7-BE-01`, AC-25) — tiles + đối chiếu hai phía điểm. */
export interface DayEndBranchRow {
  branchId: string;
  branchName: string;
  totalScans: number;
  acceptedScans: number;
  manualScans: number;
  flaggedScans: number;
  pendingConfirmationScans: number;
  reversalPendingScans: number;
  reversedScans: number;
  acceptedAmount: number;
  pointsAwardedGross: number;
  ledgerEarn: number;
  ledgerReversal: number;
  ledgerWelcomeBonus: number;
  /** Phía hóa đơn (view) khớp phía điểm (sổ cái) chưa. */
  pointsMatch: boolean;
}

export interface DayEndTotals {
  totalScans: number;
  acceptedScans: number;
  manualScans: number;
  flaggedScans: number;
  pendingConfirmationScans: number;
  reversalPendingScans: number;
  acceptedAmount: number;
  pointsAwardedGross: number;
  ledgerEarn: number;
  ledgerReversal: number;
  pointsMatch: boolean;
}

export interface DayEndReconciliation {
  businessDate: string;
  branches: DayEndBranchRow[];
  totals: DayEndTotals;
  manualScans: unknown[];
  flaggedScans: unknown[];
  pointsMatch: boolean;
}

export const reconKeys = {
  pending: ['recon', 'pending-scans'] as const,
  reversals: (status?: ReversalStatus) => ['recon', 'reversals', status ?? 'all'] as const,
  billScans: (filters: BillScanListFilters, cursor: string | null) =>
    ['recon', 'bill-scans', filters, cursor ?? 'first'] as const,
  stats: (from?: string, to?: string, branchId?: string) =>
    ['recon', 'stats', from ?? '', to ?? '', branchId ?? 'all'] as const,
  dayEnd: (date?: string, branchId?: string) =>
    ['recon', 'day-end', date ?? '', branchId ?? 'all'] as const,
};

export const reconApi = {
  listPending: () => unwrap(apiRequest<PendingMemberScan[]>('/loyalty/bill-scans/pending')),

  decidePending: (id: string, approve: boolean, rejectNote?: string) =>
    unwrap(
      apiRequest<{ billScanId: string; approved: boolean; pointsAwarded: number }>(
        `/loyalty/bill-scans/${id}/decide`,
        { method: 'POST', body: { approve, rejectNote: rejectNote ?? null } },
      ),
    ),

  getImage: (id: string) => unwrap(apiRequest<BillScanImage>(`/admin/bill-scans/${id}/image`)),

  /** Danh sách hóa đơn quét — phân trang bằng con trỏ (`S3-BE-07`). Cho phép xem lại ngày cũ. */
  listBillScans: (filters: BillScanListFilters, cursor?: string | null, limit = 10) => {
    const params = new URLSearchParams();
    (filters.branchIds ?? []).forEach((id) => params.append('branchId', id));
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (filters.status) params.set('status', filters.status);
    if (filters.source) params.set('source', filters.source);
    if (filters.hasFlags) params.set('hasFlags', 'true');
    if (filters.flag) params.set('flag', filters.flag);
    if (filters.memberId) params.set('memberId', filters.memberId);
    if (cursor) params.set('cursor', cursor);
    params.set('limit', String(limit));
    return unwrap(apiRequest<BillScanListResponse>(`/admin/bill-scans?${params}`));
  },

  stats: (from?: string, to?: string, branchId?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (branchId) params.set('branchId', branchId);
    const qs = params.toString();
    return unwrap(apiRequest<BillScanStats>(`/admin/bill-scans/stats${qs ? `?${qs}` : ''}`));
  },

  /** Đối soát cuối ngày một ngày (`S7-BE-01`). Ngày dương lịch VN; bỏ trống = hôm nay. */
  dayEnd: (date?: string, branchId?: string) => {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (branchId) params.set('branchId', branchId);
    const qs = params.toString();
    return unwrap(apiRequest<DayEndReconciliation>(`/admin/reports/reconciliation${qs ? `?${qs}` : ''}`));
  },

  /**
   * Tải CSV đối soát (UTF-8 BOM, mở được Excel). Fetch có Bearer token rồi tải blob —
   * endpoint trả tệp, không phải JSON nên không đi qua `apiRequest`. Tên tệp lấy từ
   * `Content-Disposition` server đặt.
   */
  downloadReconciliationCsv: async (date?: string, branchId?: string): Promise<void> => {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (branchId) params.set('branchId', branchId);
    const qs = params.toString();
    const token = useSession.getState().session?.accessToken;
    const res = await fetch(`${BASE_URL}/admin/reports/reconciliation.csv${qs ? `?${qs}` : ''}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new Error(`Không tải được CSV (máy chủ trả ${res.status}).`);
    }
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const fileName = match ? decodeURIComponent(match[1]) : `doi-soat-${date ?? 'hom-nay'}.csv`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  listReversals: (status?: ReversalStatus, page = 1, pageSize = 50) => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (status) params.set('status', status);
    return unwrap(apiRequest<ReversalListResponse>(`/admin/reversals?${params}`));
  },

  requestReversal: (billScanId: string, reason: string) =>
    unwrap(
      apiRequest<ReversalDetail>('/admin/reversals', {
        method: 'POST',
        body: { billScanId, reason },
      }),
    ),

  approveReversal: (id: string, note?: string) =>
    unwrap(
      apiRequest<ReversalDetail>(`/admin/reversals/${id}/approve`, {
        method: 'POST',
        body: { note: note ?? null },
      }),
    ),

  rejectReversal: (id: string, note: string) =>
    unwrap(
      apiRequest<ReversalDetail>(`/admin/reversals/${id}/reject`, {
        method: 'POST',
        body: { note },
      }),
    ),
};
